<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { AdminEventConfig, AdminOrderRecord, AdminOrderStatus } from '../../lib/types';
  import ChipGroup, { type ChipOption } from './ui/ChipGroup.svelte';
  import SurfaceCard from './ui/SurfaceCard.svelte';
  import StatusBadge from './ui/StatusBadge.svelte';

  export let orders: AdminOrderRecord[] = [];
  export let loading = false;
  export let error: string | null = null;
  export let events: AdminEventConfig[] = [];
  export let selectedEventId: string | null = null;

  const dispatch = createEventDispatcher<{
    refresh: void;
    changeEvent: { eventId: string | null };
  }>();

  const statusOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'expired', label: 'Expired' },
    { value: 'released', label: 'Released' },
    { value: 'committed', label: 'Committed' }
  ];

  let searchTerm = '';
  let statusFilter: AdminOrderStatus | 'all' = 'all';
  let eventFilter = 'all';

  $: eventOptions = events.map(event => [event.id, event.name]);
  let lastSyncedEventFilter: string | null = null;
  $: {
    const targetFilter = selectedEventId ?? 'all';
    if (eventFilter !== targetFilter && targetFilter !== lastSyncedEventFilter) {
      eventFilter = targetFilter;
    }
    lastSyncedEventFilter = targetFilter;
  }
  $: if (eventFilter !== 'all' && !events.some(event => event.id === eventFilter)) {
    eventFilter = 'all';
  }
  $: filteredOrders = applyFilters(orders, searchTerm, statusFilter, eventFilter);
  $: totalGross = filteredOrders.reduce((sum, order) => sum + (order.payment.amount ?? 0), 0);
  $: summaryCurrency = (filteredOrders[0]?.payment.currency ?? 'EUR').toUpperCase();
  $: summaryGross = totalGross > 0 ? formatAmount(totalGross, summaryCurrency) : `${summaryCurrency} ${totalGross.toFixed(2)}`;

  function applyFilters(records: AdminOrderRecord[], termValue: string, statusValue: AdminOrderStatus | 'all', eventValue: string): AdminOrderRecord[] {
    const term = termValue.trim().toLowerCase();
    return records.filter(order => {
      if (statusValue !== 'all' && order.status !== statusValue) return false;
      if (eventValue !== 'all' && order.event.id !== eventValue) return false;
      if (!term) return true;

      const haystacks = [
        order.person?.name ?? '',
        order.person?.email ?? '',
        order.ticket?.code ?? '',
        order.event.name,
        order.payment.reservationId ?? '',
        order.reservation?.id ?? '',
        order.payment.paymentIntent ?? ''
      ].map(value => value.toLowerCase());

      return haystacks.some(value => value.includes(term));
    });
  }

  function formatAmount(amount: number | null, currency: string): string {
    if (!Number.isFinite(amount ?? NaN) || (amount ?? 0) <= 0) return '—';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
      }).format(amount!);
    } catch {
      return `${currency} ${(amount ?? 0).toFixed(2)}`;
    }
  }

  function formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  function formatReservationTiming(order: AdminOrderRecord): string | null {
    const expiresInMs = order.reservation?.expiresInMs ?? null;
    if (expiresInMs === null) return null;
    if (order.status === 'expired') {
      if (expiresInMs < 0) {
        const minutes = Math.floor(Math.abs(expiresInMs) / 60000);
        return minutes > 0 ? `Expired ${minutes}m ago` : 'Expired moments ago';
      }
      return 'Expired';
    }
    if (expiresInMs === 0) return 'Expires soon';
    if (expiresInMs > 0) {
      const minutes = Math.floor(expiresInMs / 60000);
      const seconds = Math.floor((expiresInMs % 60000) / 1000);
      if (minutes > 0) return `Expires in ${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
      return `Expires in ${seconds}s`;
    }
    return null;
  }

  function mapTicketStatus(status: string): 'issued' | 'redeemed' | 'pending' | 'invited' | 'cancelled' {
    switch (status) {
      case 'redeemed':
        return 'redeemed';
      case 'pending':
        return 'pending';
      case 'invited':
        return 'invited';
      case 'cancelled':
        return 'cancelled';
      case 'issued':
      default:
        return 'issued';
    }
  }

  function handleRefresh() {
    dispatch('refresh');
  }

  function handleEventChange(event: Event) {
    const value = (event.currentTarget as HTMLSelectElement).value;
    eventFilter = value;
    dispatch('changeEvent', { eventId: value === 'all' ? null : value });
  }
</script>

<section class="orders">
  <header class="orders-header">
    <div>
      <h2>Activity</h2>
      <p class="subtitle">Audit trail of Stripe checkouts, reservation changes, and manual extras edits.</p>
      <div class="summary-chips">
        <span class="chip">{filteredOrders.length} entries</span>
        <span class="chip">{summaryGross} gross</span>
      </div>
    </div>
    <button type="button" class="ghost" on:click={handleRefresh} disabled={loading}>
      {loading ? 'Refreshing…' : 'Refresh'}
    </button>
  </header>

  {#if loading && orders.length === 0}
    <SurfaceCard>Loading activity…</SurfaceCard>
  {:else if error}
    <SurfaceCard><p class="error-text">{error}</p></SurfaceCard>
  {:else if orders.length === 0}
    <SurfaceCard>No Stripe activity yet. Completed checkouts will appear here.</SurfaceCard>
  {:else}
    <div class="orders-grid">
      <div class="main-column">
        <SurfaceCard>
          <div class="filter-stack">
            <div class="search">
              <input type="search" placeholder="Search guest, ticket, reservation" bind:value={searchTerm} />
            </div>
            <div class="chip-row">
              <div class="chip-block">
                <span>Status</span>
                <ChipGroup options={statusOptions} bind:value={statusFilter} ariaLabel="Status filter" />
              </div>
              <div class="chip-block">
                <span>Event</span>
                <select bind:value={eventFilter} on:change={handleEventChange}>
                  <option value="all">All events</option>
                  {#each eventOptions as [id, name]}
                    <option value={id}>{name}</option>
                  {/each}
                </select>
              </div>
            </div>
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <table class="orders-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Guest</th>
                <th>Event</th>
                <th>Payment</th>
                <th>Extras</th>
              </tr>
            </thead>
            <tbody>
              {#each filteredOrders as order (order.id)}
                <tr>
                  <td>
                    <div>{formatDate(order.createdAt)}</div>
                    <div class="muted small">{order.kind === 'reservation' ? 'Reservation hold' : 'Checkout'}</div>
                  </td>
                  <td>
                    <div class="status-stack">
                      <StatusBadge status={order.status} label={order.status.charAt(0).toUpperCase() + order.status.slice(1)} />
                      {#if order.kind === 'reservation'}
                        {#if formatReservationTiming(order)}
                          <div class="muted small">{formatReservationTiming(order)}</div>
                        {/if}
                      {/if}
                    </div>
                  </td>
                  <td>
                    <div>{order.person?.name ?? 'Guest'}</div>
                    {#if order.person?.email}
                      <div class="muted">{order.person.email}</div>
                    {/if}
                    {#if order.ticket}
                      <div class="muted code">{order.ticket.code}</div>
                      <StatusBadge status={mapTicketStatus(order.ticket.status)} label={order.ticket.status ?? 'Issued'} />
                    {/if}
                  </td>
                  <td>
                    <div>{order.event.name}</div>
                    {#if order.event.headline}
                      <div class="muted">{order.event.headline}</div>
                    {/if}
                  </td>
                  <td>
                    <div class="amount">{formatAmount(order.payment.amount, order.payment.currency)}</div>
                    {#if order.payment.reservationId}
                      <div class="muted small">Res {order.payment.reservationId}</div>
                    {/if}
                    {#if order.payment.paymentIntent}
                      <div class="muted small">PI: {order.payment.paymentIntent}</div>
                    {/if}
                  </td>
                  <td>
                    <div class="extras-column">
                      {#if (order.ticketExtras?.length ?? 0) > 0}
                        <ul class="extras-list">
                          {#each order.ticketExtras as extra (extra.id)}
                            <li>
                              <span>{extra.sku} ×{extra.quantity}</span>
                              {#if typeof extra.totalPrice === 'number'}
                                <span class="muted">{formatAmount(extra.totalPrice, order.payment.currency)}</span>
                              {/if}
                            </li>
                          {/each}
                        </ul>
                      {:else if (order.extras ?? []).length > 0}
                        <ul class="extras-list">
                          {#each order.extras as extra}
                            <li>
                              <span>{extra.sku} ×{extra.quantity}</span>
                              {#if typeof extra.totalPrice === 'number'}
                                <span class="muted">{formatAmount(extra.totalPrice, extra.currency ?? order.payment.currency)}</span>
                              {/if}
                            </li>
                          {/each}
                        </ul>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </SurfaceCard>
      </div>
    </div>
  {/if}
</section>

<style>
  .orders {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .orders-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .orders-header h2 {
    margin: 0;
    font-size: 1.5rem;
  }

  .orders-header .subtitle {
    margin: 0.35rem 0 0;
    color: var(--color-text-muted);
  }

  .orders-header .ghost {
    padding: 0.6rem 1rem;
    border-radius: var(--radius-lg);
    background: rgba(255, 255, 255, 0.08);
    border: none;
    color: inherit;
    cursor: pointer;
  }

  .summary-chips {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.75rem;
    border-radius: var(--radius-lg);
    background: rgba(255, 255, 255, 0.05);
    font-size: 0.85rem;
  }

  .orders-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-4);
  }

  .filter-stack {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .search input {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-lg);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-4);
  }

  .chip-block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .chip-block select {
    padding: 0.45rem 0.75rem;
    border-radius: var(--radius-lg);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .orders-table {
    width: 100%;
    border-collapse: collapse;
  }

  .orders-table thead th {
    text-align: left;
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
    color: var(--color-text-muted);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .orders-table tbody td {
    padding: 0.75rem 0.5rem;
    vertical-align: top;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .status-stack {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .muted {
    color: var(--color-text-muted);
    font-size: 0.85rem;
  }

  .muted.small {
    font-size: 0.75rem;
  }

  .muted.code {
    font-family: 'Source Code Pro', monospace;
  }

  .amount {
    font-weight: 600;
  }

  .extras-column {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .extras-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .error-text {
    color: #ff9a9a;
    margin: 0;
  }

  @media (max-width: 1024px) {
    .orders-header {
      flex-direction: column;
      gap: var(--space-3);
    }
  }
</style>
